import { useEffect, useState } from "react";
import { MainLayout } from "../../layout/MainLayout";
import { Button, Form, InputGroup } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { Faq, emptyFaq } from "../../interfaces/Faq";
import FaqDelete from "./FaqDelete";
import FaqEdit from "./FaqEdit";
import FaqAdd from "./FaqAdd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../../elements/LoadingSpinner";

export default function Faqs() {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const isAdmin = localStorage.getItem('isAdmin');
    
    const [faqs, setFaqs] = useState<Faq[]>([]);
    const [selectedFaq, setSelectedFaq] = useState<Faq>(emptyFaq);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reloadData, setReloadData] = useState(0);

    // Search and sort states
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('weight');
    const [isSearching, setIsSearching] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const columns: TableColumn<Faq>[] = [
        {
            name: t("Id"),
            selector: (row: Faq) => row._id,
            sortable: true,
            width: '200px',
            omit: true,
        },
        {
            name: t("Title"),
            selector: (row: Faq) => {
                const defaultTranslation = row.translations.find(t => t.lang === row.defaultLang) || row.translations[0];
                return defaultTranslation?.title || '-';
            },
            sortable: true,
            grow: 2,
        },
        {
            name: t("Weight"),
            selector: (row: Faq) => row.weight,
            sortable: true,
            width: '100px',
        },
        {
            name: t("Languages"),
            selector: (row: Faq) => row.translations.map(t => t.lang.toUpperCase()).join(', '),
            sortable: false,
            width: '150px',
        },
        {
            name: t("Actions"),
            cell: (row: Faq) => (
                <div className="actions">
                    <a
                        className="edit-link"
                        title={t("Edit FAQ")}
                        href="#"
                        onClick={(event) => handleShowEditForm(event, row._id)}
                    ></a>
                    <a
                        className="delete-link"
                        title={t("Delete FAQ")}
                        href="#"
                        onClick={(event) => handleShowDeleteForm(event, row._id)}
                    ></a>
                </div>
            ),
            width: '150px',
        },
    ];

    const ExpandedComponent = ({ data }: { data: Faq }) => (
        <div style={{ padding: '15px' }}>
            <div><strong>{t("Id")}:</strong> {data._id}</div>
        </div>
    );

    const fetchFaqs = () => {
        const params = new URLSearchParams();
        if (searchQuery.trim()) {
            params.append('search', searchQuery.trim());
        }
        if (sortBy) {
            params.append('sort', sortBy);
        }

        const queryString = params.toString();
        const url = import.meta.env.VITE_API_URL + '/v1/faq' + (queryString ? '?' + queryString : '');

        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };

        setLoading(true);
        setIsSearching(true);
        fetch(url, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                setFaqs(data);
                setLoading(false);
                setIsSearching(false);
            })
            .catch(() => {
                setLoading(false);
                setIsSearching(false);
            });
    };

    useEffect(() => {
        fetchFaqs();
    }, [sortBy, reloadData]);

    const handleShowAddForm = () => {
        setShowAddForm(true);
    }

    const handleHideAddForm = () => {
        setShowAddForm(false);
    }

    const handleSumbitAddForm = (f: Faq) => {
        setShowAddForm(false);
        setReloadData(prev => prev + 1);
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        fetchFaqs();
    }

    const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSortBy(e.target.value);
    }

    const handleClearFilters = () => {
        setSearchQuery('');
        setSortBy('weight');
        // fetchFaqs will be called by useEffect when sortBy changes
    }

    const handleToggleFilters = () => {
        setShowFilters(!showFilters);
    }
    
    const handleShowDeleteForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedFaq: Faq = faqs.find((f: Faq) => f._id == id) ?? emptyFaq;
        if (newSelectedFaq._id != '') {
            setSelectedFaq(newSelectedFaq);
            setShowDeleteForm(true);
        } else {
            alert(t("No FAQ selected."));
        }
    }
    
    const handleHideDeleteForm = () => {
        setShowDeleteForm(false);
    }

    const handleSumbitDeleteForm = (id: string) => {
        setShowDeleteForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowEditForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedFaq: Faq = faqs.find((f: Faq) => f._id == id) ?? emptyFaq;
        if (newSelectedFaq._id != '') {
            setSelectedFaq(newSelectedFaq);
            setShowEditForm(true);
        } else {
            alert(t("No FAQ selected."));
        }
    }
    
    const handleHideEditForm = () => {
        setShowEditForm(false);
    }

    const handleSumbitEditForm = (faq: Faq) => {
        setShowEditForm(false);
        setReloadData(prev => prev + 1);
    }

    return (
        <MainLayout>
            <div className="container-fluid">
                <div className="row">
                    { isAdmin &&
                        <div className="admin-faqs-container">
                                    <div className="page-header">
                                        <h1>{ t("FAQ management") }</h1>
                                        <div className="page-actions">
                                            <button
                                                className={`filter-toggle ${showFilters ? 'active' : 'inactive'}`}
                                                onClick={handleToggleFilters}
                                                title={t("Filters")}
                                            >
                                                <i className="bi bi-funnel"></i>
                                            </button>
                                            <a className="action-icon" onClick={ handleShowAddForm } title={ t("New FAQ") }>
                                                <i className="bi bi-plus-circle"></i>
                                            </a>
                                        </div>
                                    </div>

                                    {/* Search and filter controls */}
                                    <div className={`faq-filters ${!showFilters ? 'hidden' : ''}`}>
                                        <Form onSubmit={handleSearchSubmit}>
                                            <div className="filter-controls">
                                                <Form.Group className="search-group">
                                                    <Form.Label>{ t("Search") }</Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder={ t("Search in all fields...") }
                                                            value={searchQuery}
                                                            onChange={handleSearchChange}
                                                        />
                                                        <Button variant="primary" type="submit" disabled={isSearching}>
                                                            {isSearching ? t("Searching...") : t("Search")}
                                                        </Button>
                                                    </InputGroup>
                                                </Form.Group>

                                                <Form.Group className="sort-group">
                                                    <Form.Label>{ t("Sort by") }</Form.Label>
                                                    <Form.Select value={sortBy} onChange={handleSortChange}>
                                                        <option value="weight">{ t("Weight") } (↑)</option>
                                                        <option value="-weight">{ t("Weight") } (↓)</option>
                                                        <option value="title">{ t("Title") } (A-Z)</option>
                                                        <option value="-title">{ t("Title") } (Z-A)</option>
                                                    </Form.Select>
                                                </Form.Group>

                                                {(searchQuery || sortBy !== 'weight') && (
                                                    <Button variant="secondary" onClick={handleClearFilters}>
                                                        { t("Clear filters") }
                                                    </Button>
                                                )}
                                            </div>
                                        </Form>

                                        <div className="filter-info">
                                            { t("Total results") }: <strong>{faqs.length}</strong>
                                            {searchQuery && (
                                                <span className="search-term">
                                                    { t("Searching for") }: <strong>"{searchQuery}"</strong>
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="admin-faqs-table-container">
                                        <DataTable
                                            columns={columns}
                                            data={faqs}
                                            progressPending={loading}
                                            progressComponent={<LoadingSpinner />}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                            paginationComponentOptions={{
                                                rowsPerPageText: t('Rows per page') + ':',
                                                rangeSeparatorText: t('of'),
                                            }}
                                            highlightOnHover
                                            striped
                                            responsive
                                            noDataComponent={t("No FAQs found")}
                                            expandableRows
                                            expandableRowsComponent={ExpandedComponent}
                                        />
                                    </div>
                                </div>
                            }
                    { !isAdmin &&
                        <div className="admin-faqs-container">
                            { t("You are not allowed to access this section.") }
                        </div>
                    }
                </div>
            </div>
            { showAddForm &&
                <FaqAdd submitCallback={ handleSumbitAddForm } closeCallback={ handleHideAddForm } />
            }
            { showDeleteForm &&
                <FaqDelete faq={ selectedFaq } submitCallback={ handleSumbitDeleteForm } closeCallback={ handleHideDeleteForm } />
            }
            { showEditForm &&
                <FaqEdit faq={ selectedFaq } submitCallback={ handleSumbitEditForm } closeCallback={ handleHideEditForm } />
            }
        </MainLayout>);
}